/**
 * ImportModal - JSON Import Dialog with Drag & Drop
 *
 * Uses Mantine Modal + Dropzone for file upload.
 * Validates imported JSON and writes to localStorage.
 */

import { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Text,
  Alert,
  List,
  Group,
  Button,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import {
  IconFileImport,
  IconUpload,
  IconX,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { parseImportFile, writeToLocalStorage } from '../../utils/jsonImport';
import type { ImportResult } from '../../types';

export interface ImportModalProps {
  opened: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

export function ImportModal({ opened, onClose, onImportSuccess }: ImportModalProps) {
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDrop = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setIsProcessing(true);
    setImportResult(null);

    const result = await parseImportFile(file);
    setImportResult(result);

    if (result.success && result.data) {
      writeToLocalStorage(result.data);
      // Short delay so user can see success message before remount
      setTimeout(() => {
        onImportSuccess();
        onClose();
      }, 1200);
    }

    setIsProcessing(false);
  }, [onImportSuccess, onClose]);

  const handleReject = useCallback(() => {
    setImportResult({
      success: false,
      errors: ['Nieprawidłowy typ pliku. Akceptowany format: JSON.'],
      warnings: [],
    });
  }, []);

  const handleClose = useCallback(() => {
    setImportResult(null);
    setIsProcessing(false);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconFileImport size={20} />
          <Text fw={600}>Importuj konfigurację</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Przeciągnij plik JSON z wcześniej wyeksportowaną konfiguracją symulatora.
          Import nadpisze bieżące dane w przeglądarce.
        </Text>

        <Dropzone
          onDrop={handleDrop}
          onReject={handleReject}
          maxSize={1024 * 1024} // 1MB
          accept={['application/json']}
          multiple={false}
          loading={isProcessing}
          data-testid="import-dropzone"
        >
          <Stack gap="xs" align="center" py="xl" style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconCheck size={40} color="var(--mantine-color-green-6)" />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} color="var(--mantine-color-red-6)" />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconUpload size={40} color="var(--mantine-color-dimmed)" />
            </Dropzone.Idle>

            <Text size="md" ta="center">
              Przeciągnij plik JSON tutaj
            </Text>
            <Text size="xs" c="dimmed" ta="center">
              lub kliknij, aby wybrać plik (maks. 1MB)
            </Text>
          </Stack>
        </Dropzone>

        {/* Success */}
        {importResult?.success && (
          <Alert
            icon={<IconCheck size={16} />}
            title="Import zakończony"
            color="green"
            data-testid="import-success"
          >
            Konfiguracja została zaimportowana. Strona zostanie odświeżona.
            {importResult.warnings.length > 0 && (
              <List size="xs" mt="xs">
                {importResult.warnings.map((w, i) => (
                  <List.Item key={i}>{w}</List.Item>
                ))}
              </List>
            )}
          </Alert>
        )}

        {/* Errors */}
        {importResult && !importResult.success && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Błąd importu"
            color="red"
            data-testid="import-error"
          >
            <List size="xs">
              {importResult.errors.map((e, i) => (
                <List.Item key={i}>{e}</List.Item>
              ))}
            </List>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            Zamknij
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
